import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';

import { manualApi } from '../api/manual';
import { formatPeriodRange } from '../utils/dates';

dayjs.locale('ru');

const SOURCE_OPTIONS = [
  { value: 'ANY', label: 'Все источники' },
  { value: 'TELEGRAM', label: 'Telegram' },
  { value: 'IMPORT', label: 'Файл' },
  { value: 'STAFF_USB', label: 'Сбор сотрудником' },
];

const MONTH_LABELS = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
const WEAK_PROGRESS_THRESHOLD = 0.7;

const progressRatio = (row) => {
  const total = Number(row?.total_devices) || 0;
  if (total <= 0) return null;
  const submitted = Math.max(0, Number(row?.submitted_devices) || 0);
  return Math.min(submitted / total, 1);
};

const heatApartmentProgressRatio = (row) => {
  const total = Number(row?.heat_total_apartments) || 0;
  if (total <= 0) return null;
  const completed = Math.max(0, Number(row?.heat_completed_apartments) || 0);
  return Math.min(completed / total, 1);
};

const apartmentProgressRatio = (row) => {
  const total = Math.max(
    0,
    (Number(row?.total_apartments) || 0) - (Number(row?.no_devices_apartments) || 0)
  );
  if (total <= 0) return null;
  const completed = Math.max(0, Number(row?.completed_apartments) || 0);
  return Math.min(completed / total, 1);
};

const apartmentProgressCounts = (row) => ({
  completed: Math.max(0, Number(row?.completed_apartments) || 0),
  total: Math.max(
    0,
    (Number(row?.total_apartments) || 0) - (Number(row?.no_devices_apartments) || 0)
  ),
});

const percent = (value) => Math.round(Math.max(0, Math.min(Number(value) || 0, 1)) * 100);

const isClosedPeriod = (row) => {
  return Boolean(row?.period_end) && dayjs(row.period_end).isBefore(dayjs());
};
const isStartedPeriod = (row) => !row?.period_start || !dayjs(row.period_start).isAfter(dayjs());
const isAvailablePeriod = (row) => (row?.availability || 'available') === 'available';

const periodStage = (row) => {
  if (!row || !isStartedPeriod(row)) return { label: 'Не начался', color: 'default' };
  if (row.reading_window_start && dayjs().isBefore(dayjs(row.reading_window_start))) {
    return { label: 'Ожидание сбора', color: 'default' };
  }
  if (row.period_end && !dayjs(row.period_end).isBefore(dayjs())) {
    return { label: 'Сбор идёт', color: 'primary' };
  }
  if (!isClosedPeriod(row)) return { label: 'Добор', color: 'warning' };
  return { label: 'Завершён', color: 'default' };
};

const isWeakPeriod = (row) => {
  const ratio = progressRatio(row);
  return ratio !== null && isAvailablePeriod(row) && isClosedPeriod(row) && ratio < WEAK_PROGRESS_THRESHOLD;
};

const progressColor = (value) => {
  const next = percent(value);
  if (next >= 95) return '#16A34A';
  if (next >= 70) return '#2563EB';
  if (next >= 40) return '#F59E0B';
  return '#EF4444';
};

const progressTone = (value) => {
  const next = percent(value);
  if (next >= 95) return 'rgba(22,163,74,0.12)';
  if (next >= 70) return 'rgba(37,99,235,0.12)';
  if (next >= 40) return 'rgba(245,158,11,0.14)';
  return 'rgba(239,68,68,0.12)';
};

function CompactProgressLine({ label, value, tooltip }) {
  return (
    <Tooltip title={tooltip}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Typography
          variant="caption"
          sx={{ minWidth: 86, color: 'text.secondary', fontWeight: 700, lineHeight: 1 }}
        >
          {label}
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <LinearProgress
            variant="determinate"
            value={value === null ? 0 : percent(value)}
            sx={{
              height: 4,
              borderRadius: 4,
              backgroundColor: 'rgba(148,163,184,0.14)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                backgroundColor: value === null ? '#CBD5E1' : progressColor(value),
              },
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ minWidth: 36, fontWeight: 850, textAlign: 'right' }}>
          {value === null ? '—' : `${percent(value)}%`}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

function formatPeriod(start, end, timeZone) {
  return formatPeriodRange(start, end, { timeZone });
}

export default function ReadingsProgressPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [viewMode, setViewMode] = useState('month');
  const [monthValue, setMonthValue] = useState(dayjs().startOf('month'));
  const [filters, setFilters] = useState({
    year: dayjs().year(),
    month: dayjs().format('YYYY-MM'),
    source: 'ANY',
  });

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await manualApi.getSubmissionProgressSummary({
        year: Number(filters.year),
        month: filters.month || undefined,
        source: filters.source,
      });
      setData(response);
    } catch (err) {
      console.error('load readings progress summary failed', err);
      setError(err?.response?.data?.detail || 'Не удалось загрузить прогресс сбора показаний.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const buildings = useMemo(() => data?.buildings || [], [data]);
  const dataStartDate = useMemo(() => {
    const value = dayjs(data?.data_available_from);
    return value.isValid() ? value.startOf('day') : null;
  }, [data?.data_available_from]);
  const minYear = dataStartDate?.year() || 2000;

  useEffect(() => {
    if (!dataStartDate || !monthValue?.isValid?.() || !monthValue.isBefore(dataStartDate, 'month')) return;
    const nextMonth = dataStartDate.startOf('month');
    setMonthValue(nextMonth);
    setFilters((prev) => ({
      ...prev,
      year: nextMonth.year(),
      month: nextMonth.format('YYYY-MM'),
    }));
  }, [dataStartDate, monthValue]);

  const totals = useMemo(() => {
    const current = buildings
      .map((item) => item.current_month)
      .filter((item) => item && item.availability !== 'before_start');
    const totalDevices = current.reduce((sum, row) => sum + (row.total_devices || 0), 0);
    const submittedDevices = current.reduce((sum, row) => sum + (row.submitted_devices || 0), 0);
    const totalApartments = current.reduce(
      (sum, row) => sum + apartmentProgressCounts(row).total,
      0
    );
    const completedApartments = current.reduce((sum, row) => sum + (row.completed_apartments || 0), 0);
    const weakMonths = buildings.reduce(
      (sum, building) => sum + (building.months || []).filter(isWeakPeriod).length,
      0
    );
    return {
      totalDevices,
      submittedDevices,
      totalApartments,
      completedApartments,
      apartmentProgress: totalApartments
        ? Math.min(completedApartments / totalApartments, 1)
        : null,
      progress: totalDevices ? Math.min(submittedDevices / totalDevices, 1) : null,
      weakMonths,
    };
  }, [buildings]);

  const changeYear = (rawValue) => {
    const nextYear = Number(rawValue);
    if (!Number.isInteger(nextYear) || nextYear < minYear || nextYear > 2100) return;
    const baseMonth = monthValue?.isValid?.() ? monthValue : dayjs().startOf('month');
    const nextMonth = baseMonth.year(nextYear).startOf('month');
    setMonthValue(nextMonth);
    setFilters((prev) => ({
      ...prev,
      year: nextYear,
      month: nextMonth.format('YYYY-MM'),
    }));
  };

  const sortedBuildings = useMemo(() => {
    const collator = new Intl.Collator('ru-RU', { numeric: true, sensitivity: 'base' });
    return [...buildings].sort((a, b) => {
      const complexCompare = collator.compare(a.residential_complex_name || '', b.residential_complex_name || '');
      if (complexCompare !== 0) return complexCompare;
      return collator.compare(a.building_name || '', b.building_name || '');
    });
  }, [buildings]);

  return (
    <Box className="home-container" sx={{ mx: 3, pb: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 3,
          border: '1px solid rgba(15,23,42,0.08)',
          background: 'var(--surface-bg)',
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <DatePicker
                views={['year', 'month']}
                openTo="month"
                label="Отчётный месяц"
                minDate={dataStartDate || undefined}
                value={monthValue}
                onChange={(value) => {
                  if (!value || !value.isValid?.()) {
                    setMonthValue(null);
                    setFilters((prev) => ({ ...prev, month: '' }));
                    return;
                  }
                  const nextMonth = value.startOf('month');
                  setMonthValue(nextMonth);
                  setFilters((prev) => ({
                    ...prev,
                    month: nextMonth.format('YYYY-MM'),
                    year: nextMonth.year(),
                  }));
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 154, background: '#fff', borderRadius: 1 },
                  },
                  popper: {
                    placement: 'bottom-start',
                  },
                }}
              />
              <TextField
                size="small"
                label="Год"
                type="number"
                value={filters.year}
                onChange={(event) => changeYear(event.target.value)}
                inputProps={{ min: minYear, max: 2100 }}
                sx={{ width: 110, background: '#fff', borderRadius: 1 }}
              />
              <TextField
                select
                size="small"
                label="Источник"
                value={filters.source}
                onChange={(event) => setFilters((prev) => ({ ...prev, source: event.target.value }))}
                sx={{ width: 150, background: '#fff', borderRadius: 1 }}
              >
                {SOURCE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <Chip
                size="small"
                icon={<CalendarMonthRoundedIcon />}
                label={`Дома: ${buildings.length}`}
                variant="outlined"
                sx={{ height: 34, fontWeight: 800, background: '#fff' }}
              />
              {dataStartDate ? (
                <Tooltip title="Более ранние периоды недоступны и не учитываются как просадки">
                  <Chip
                    size="small"
                    label={`Учёт с ${dataStartDate.format('DD.MM.YYYY')}`}
                    variant="outlined"
                    sx={{ height: 34, fontWeight: 800, color: '#475569', background: '#F8FAFC' }}
                  />
                </Tooltip>
              ) : null}
              <Tooltip title="Доля приборов, от которых получено хотя бы одно показание за выбранный период">
                <Chip
                  size="small"
                  label={`Собрано: ${totals.progress === null ? '—' : `${percent(totals.progress)}%`}`}
                  sx={{
                    height: 34,
                    fontWeight: 900,
                    color: totals.progress === null ? '#64748B' : progressColor(totals.progress),
                    background: totals.progress === null ? '#F1F5F9' : progressTone(totals.progress),
                    border: `1px solid ${totals.progress === null ? '#CBD5E1' : `${progressColor(totals.progress)}33`}`,
                  }}
                />
              </Tooltip>
              <Chip
                size="small"
                label={`Приборы: ${totals.submittedDevices}/${totals.totalDevices}`}
                variant="outlined"
                sx={{ height: 34, fontWeight: 800, background: '#fff' }}
              />
              <Tooltip title="Квартира считается собранной, когда получены показания от всех установленных в ней приборов">
                <Chip
                  size="small"
                  label={`Квартиры: ${totals.completedApartments}/${totals.totalApartments}${totals.apartmentProgress === null ? '' : ` · ${percent(totals.apartmentProgress)}%`}`}
                  variant="outlined"
                  sx={{
                    height: 34,
                    fontWeight: 800,
                    color: totals.apartmentProgress === null
                      ? '#64748B'
                      : progressColor(totals.apartmentProgress),
                    background: '#fff',
                  }}
                />
              </Tooltip>
              <Tooltip title="Количество завершённых периодов по домам, в которых собрано менее 70% показаний. Текущий и будущие периоды не учитываются">
                <Chip
                  size="small"
                  color={totals.weakMonths ? 'warning' : 'success'}
                  label={`Просадки: ${totals.weakMonths}`}
                  variant={totals.weakMonths ? 'filled' : 'outlined'}
                  sx={{ height: 34, fontWeight: 800 }}
                />
              </Tooltip>
            </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup
              size="small"
              value={viewMode}
              exclusive
              onChange={(event, value) => value && setViewMode(value)}
            >
              <ToggleButton value="month">Месяц</ToggleButton>
              <ToggleButton value="year">Год</ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={loadSummary}
              disabled={loading}
            >
              Обновить
            </Button>
          </Stack>
          </Stack>
        </LocalizationProvider>
      </Paper>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          borderColor: 'rgba(15,23,42,0.08)',
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        <TableContainer sx={{ maxHeight: 'calc(100vh - 210px)', overflowX: 'auto' }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: viewMode === 'year' ? 1180 : 880,
              '& .MuiTableCell-root': { py: 0.85, px: 1.15 },
              '& .MuiTableHead-root .MuiTableCell-root': {
                fontSize: 12,
                fontWeight: 900,
                color: '#475569',
                backgroundColor: '#F8FAFC',
                borderBottom: '1px solid rgba(148,163,184,0.28)',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Дом</TableCell>
                <TableCell>ЖК</TableCell>
                {viewMode === 'month' ? (
                  <>
                    <TableCell>Период</TableCell>
                    <TableCell sx={{ minWidth: 230 }}>Прогресс</TableCell>
                    <TableCell>Квартиры</TableCell>
                    <TableCell>Приборы</TableCell>
                    <TableCell>Проблемы</TableCell>
                  </>
                ) : (
                  MONTH_LABELS.map((label) => (
                    <TableCell key={label} align="center">{label}</TableCell>
                  ))
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 8 }).map((_, index) => (
                  <TableRow key={`progress-skeleton-${index}`}>
                    <TableCell><Skeleton width={180} /></TableCell>
                    <TableCell><Skeleton width={160} /></TableCell>
                    <TableCell colSpan={viewMode === 'year' ? 12 : 5}><Skeleton height={28} /></TableCell>
                  </TableRow>
                ))
              ) : sortedBuildings.length ? (
                sortedBuildings.map((building) => {
                  const current = building.current_month;
                  const currentProgress = progressRatio(current);
                  const currentApartmentProgress = apartmentProgressRatio(current);
                  const currentApartmentCounts = apartmentProgressCounts(current);
                  const currentHeatProgress = heatApartmentProgressRatio(current);
                  const weakCount = (building.months || []).filter(isWeakPeriod).length;
                  const currentPeriodStage = periodStage(current);
                  return (
                    <TableRow
                      key={building.building_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        const params = new URLSearchParams({
                          month: filters.month,
                          source: filters.source,
                        });
                        navigate(
                          `/building/${building.building_id}/manual-progress?${params.toString()}`
                        );
                      }}
                    >
                      <TableCell>
                        <Stack spacing={0.15}>
                          <Typography variant="body2" sx={{ fontWeight: 850 }}>
                            {building.building_name || 'Дом'}
                          </Typography>
                          {building.building_address ? (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 320 }}>
                              {building.building_address}
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>{building.residential_complex_name || '—'}</TableCell>
                      {viewMode === 'month' ? (
                        current?.availability === 'before_start' ? (
                          <TableCell colSpan={5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" label="До начала учёта" sx={{ fontWeight: 800 }} />
                              <Typography variant="caption" color="text.secondary">
                                Показания за этот период не ожидались
                              </Typography>
                            </Stack>
                          </TableCell>
                        ) : <>
                          <TableCell>
                            <Stack spacing={0.2}>
                              <Typography variant="body2">
                                {formatPeriod(current?.period_start, current?.period_end, building.timezone)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Опорная дата: {building.report_anchor_day || 25} число
                                {' · '}
                                показания: {formatPeriod(
                                  current?.reading_window_start,
                                  current?.period_end,
                                  building.timezone
                                )}
                              </Typography>
                              {current?.availability === 'partial' ? (
                                <Chip
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  label="Неполный первый период"
                                  sx={{ alignSelf: 'flex-start', height: 22, fontWeight: 750 }}
                                />
                              ) : null}
                              <Chip
                                size="small"
                                color={currentPeriodStage.color}
                                variant="outlined"
                                label={currentPeriodStage.label}
                                sx={{ alignSelf: 'flex-start', height: 22, fontWeight: 750 }}
                              />
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.65}>
                              <Tooltip title="Доля приборов, от которых получены показания за выбранный период">
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Box sx={{ flexGrow: 1 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={currentProgress === null ? 0 : percent(currentProgress)}
                                      sx={{
                                        height: 9,
                                        borderRadius: 8,
                                        backgroundColor: 'rgba(148,163,184,0.16)',
                                        '& .MuiLinearProgress-bar': {
                                          borderRadius: 8,
                                          backgroundColor: currentProgress === null ? '#CBD5E1' : progressColor(currentProgress),
                                        },
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="body2" sx={{ minWidth: 42, fontWeight: 850, textAlign: 'right' }}>
                                    {currentProgress === null ? '—' : `${percent(currentProgress)}%`}
                                  </Typography>
                                </Stack>
                              </Tooltip>
                              <CompactProgressLine
                                label="Квартиры"
                                value={currentApartmentProgress}
                                tooltip={currentApartmentProgress === null
                                  ? 'В доме нет квартир с установленными приборами'
                                  : `Квартира считается собранной, когда получены показания от всех установленных в ней приборов. Полностью собрано ${currentApartmentCounts.completed} из ${currentApartmentCounts.total}.`}
                              />
                              {building.heat_allocator_enabled ? (
                                <CompactProgressLine
                                  label="Квартиры УРТ"
                                  value={currentHeatProgress}
                                  tooltip={currentHeatProgress === null
                                    ? 'В выбранном периоде квартиры с УРТ не найдены'
                                    : `Квартира считается собранной, когда получены показания от всех установленных в ней УРТ. Полностью собрано ${current.heat_completed_apartments} из ${current.heat_total_apartments}.`}
                                />
                              ) : null}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {currentApartmentCounts.completed}/{currentApartmentCounts.total}
                          </TableCell>
                          <TableCell>
                            {current?.submitted_devices || 0}/{current?.total_devices || 0}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {current?.partial_apartments ? <Chip size="small" color="warning" label={`Частично: ${current.partial_apartments}`} /> : null}
                              {current?.empty_apartments ? <Chip size="small" color="error" label={`Нет: ${current.empty_apartments}`} /> : null}
                              {current?.no_devices_apartments ? <Chip size="small" variant="outlined" label={`Без приборов: ${current.no_devices_apartments}`} /> : null}
                              {weakCount ? (
                                <Tooltip title="Завершённые периоды текущего года со сбором ниже 70%">
                                  <Chip size="small" variant="outlined" label={`Просадки за год: ${weakCount}`} />
                                </Tooltip>
                              ) : null}
                              {!current?.partial_apartments && !current?.empty_apartments && !current?.no_devices_apartments && !weakCount ? <Chip size="small" color="success" label="Без просадок" /> : null}
                            </Stack>
                          </TableCell>
                        </>
                      ) : (
                        (building.months || []).map((month, index) => {
                          const monthProgress = progressRatio(month);
                          const monthApartmentProgress = apartmentProgressRatio(month);
                          const monthApartmentCounts = apartmentProgressCounts(month);
                          const monthHeatProgress = heatApartmentProgressRatio(month);
                          const availability = month.availability || 'available';
                          const beforeStart = availability === 'before_start';
                          const partialPeriod = availability === 'partial';
                          const hasData = monthProgress !== null && isStartedPeriod(month);
                          const periodClosed = isClosedPeriod(month);
                          const apartmentDetails = monthApartmentProgress === null
                            ? ' · квартиры: нет приборов'
                            : ` · квартиры: ${percent(monthApartmentProgress)}% (${monthApartmentCounts.completed}/${monthApartmentCounts.total})`;
                          const heatDetails = building.heat_allocator_enabled
                            ? monthHeatProgress === null
                              ? ' · квартиры УРТ: нет данных'
                              : ` · квартиры УРТ: ${percent(monthHeatProgress)}% (${month.heat_completed_apartments}/${month.heat_total_apartments})`
                            : '';
                          return (
                            <TableCell key={month.month || index} align="center">
                              <Tooltip
                                title={hasData
                                  ? `${month.month}: приборы ${percent(monthProgress)}% (${month.submitted_devices}/${month.total_devices})${apartmentDetails}${heatDetails}${partialPeriod ? ' · неполный первый период, в просадки не входит' : periodClosed ? '' : ' · период ещё идёт'}`
                                  : beforeStart
                                    ? `${month.month}: учёт ещё не вёлся`
                                    : `${month.month}: данных пока нет`}
                              >
                                <Box
                                  sx={{
                                    mx: 'auto',
                                    width: 52,
                                    height: 34,
                                    borderRadius: 2,
                                    display: 'grid',
                                    placeItems: 'center',
                                    fontSize: 12,
                                    fontWeight: 900,
                                    color: beforeStart ? '#94A3B8' : hasData ? progressColor(monthProgress) : '#94A3B8',
                                    background: beforeStart
                                      ? 'repeating-linear-gradient(135deg, #F8FAFC, #F8FAFC 5px, #F1F5F9 5px, #F1F5F9 10px)'
                                      : hasData ? progressTone(monthProgress) : '#F8FAFC',
                                    border: partialPeriod
                                      ? '1px dashed #D97706'
                                      : `1px solid ${hasData ? `${progressColor(monthProgress)}33` : '#E2E8F0'}`,
                                  }}
                                >
                                  {beforeStart ? 'до' : hasData ? `${percent(monthProgress)}%` : '—'}
                                </Box>
                              </Tooltip>
                            </TableCell>
                          );
                        })
                      )}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={viewMode === 'year' ? 14 : 7} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">Данных пока нет.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
